// ABOUTME: The InMemoryCache type policies of the main Apollo client.
// ABOUTME: Kept apart from the client so cache tests run against the real policies without its link chain.
import type { StoreObject, TypePolicies } from "@apollo/client/cache"
import { readCalendarItemsStrippingPending } from "./pendingCalendarItemDeletes"
import { userOwnedQueryFields, userOwnedTypePolicies } from "./userOwnedEntities"
import { calendarItemsKeyArgs } from "./calendarItemsKeyArgs"

// Cache configuration with type policies for proper merging
export const typePolicies: TypePolicies = {
  /**
   * The product catalogue is a value, not an entity we own. It has an `id`,
   * so Apollo normalised it — and every product row of a menu holds the same
   * `BaseIngredient` in its `info`. Searching for a product then rewrote the
   * entities the open menu was watching, which re-rendered the whole ration
   * editor: the row under the search panel remounted and took the panel with
   * it, right as the results arrived. Stored inline, the catalogue's answer
   * can no longer reach into a menu somebody is editing.
   */
  BaseIngredient: { keyFields: false },
  /**
   * `info` is a `BaseIngredient`, which stays inline for the reason above — so
   * it is a plain object on the record and an incoming one would REPLACE it.
   * The calendar's slim selection leaves `nutrients` out (45 fields per
   * ingredient it does not need), so a week's answer landing after the editor's
   * full one used to take them with it and the editor had to fetch the menu
   * again. Merged field by field, the slim answer updates what it carries and
   * leaves the rest standing.
   */
  Ingredient: {
    fields: {
      info: {
        /**
         * Merged, but only while it is the same product. The row keeps its id
         * when the athlete swaps oats for butter, and an unconditional merge
         * then kept the new product's id and description beside the old one's
         * nutrients — a row reading back as butter at 380 kcal, complete and
         * wrong. Different product: take the incoming one as it is, and the
         * missing nutrients make the read a miss, which is what fetches them.
         */
        merge(existing: StoreObject | undefined, incoming: StoreObject, { mergeObjects, readField }) {
          if (!existing) return incoming

          const sameProduct = readField("id", existing) === readField("id", incoming)
          return sameProduct ? mergeObjects(existing, incoming) : incoming
        },
      },
    },
  },
  Query: {
    fields: {
      // Keyed by userID, and answered from the one `User:<id>` record (see the User policy below).
      // The field holds a reference now, so the deep merge that used to join two queries' answers
      // by hand is gone: the record does that joining, and for every field, not just the top level.
      user: {
        keyArgs: ["userID"],
      },
      // Keyed by userID, and answered from the one record the schema promises
      // these are (see userOwnedEntities). A miss, or a record short of the
      // fields asked for, falls through to the network the way it always did.
      ...userOwnedQueryFields,
      /**
       * A declared merge, so replacing the list is not a "cache data may be
       * lost" warning — that is all this policy was ever for.
       *
       * It used to keep `existing` for an EMPTY incoming list as well, which
       * came along with the warning fix in January and quietly made "no plans"
       * unrepresentable: delete the last plan (or open an account that has
       * none) and the cache kept showing the previous list, refetch or not.
       * An empty answer is an answer.
       */
      planItems: {
        merge(existing: unknown, incoming: unknown) {
          if (!incoming) {
            return existing
          }
          return incoming
        },
      },
      // Calendar items are keyed by userID and dates to prevent cache collisions between users
      calendarItems: {
        keyArgs: calendarItemsKeyArgs,
        merge(existing: unknown, incoming: unknown) {
          if (!incoming) {
            return existing
          }
          return incoming
        },
        // Strip calendar items whose optimistic delete is still inside its 5s undo window (see
        // pendingCalendarItemDeletes). This makes the optimistic removal authoritative for EVERY
        // read of the field, so a server-truth re-read from any source (create, drag, refetch,
        // poll) cannot resurrect a just-deleted card on screen. Hot path (nothing pending) returns
        // the stored value untouched.
        read: readCalendarItemsStrippingPending,
      },
      // Folders are keyed by variant and userID to prevent cache collisions between different folder types and users
      folders: {
        keyArgs: ["p", ["variant", "userID"]],
      },
      // Activity library items are keyed by folderID to prevent cache collisions between folders
      activityLibraryItems: {
        keyArgs: ["params", ["folderID"]],
        merge(_existing: unknown, incoming: unknown) {
          return incoming
        },
      },
    },
  },
  /**
   * The User is one record per person, keyed by its `id` (Apollo's default, so no policy is
   * needed here). It used to be stored inline — `keyFields: false` — against "cross-user data
   * mixing", but that fear does not survive reading the queries: both operations that read
   * `user(userID:)` select `id`, so two people are two records and cannot mix. What the inline
   * copy did cost was real: a mutation answering with a `User` had nowhere to land, so every
   * such screen had to refetch the whole user afterwards.
   */
  // Types without ID fields - disable normalization to prevent cross-user mixing
  Permissions: {
    keyFields: false,
  },
  // UserSettings/UserParameters do carry an id now — the owner's userID — so
  // they are normalized instead: one record per user (see userOwnedEntities).
  ...userOwnedTypePolicies,
  Trainer: {
    keyFields: false,
  },
  TrainerCommonInfo: {
    keyFields: false,
  },
  TrainerPersonalInfo: {
    keyFields: false,
  },
  TrainingOptions: {
    keyFields: false,
  },
  TrainingTarget: {
    keyFields: false,
  },
  NutritionTarget: {
    keyFields: false,
  },
  NotificationSettings: {
    keyFields: false,
  },
  /**
   * Параметры профиля активности — встроенный объект, а не сущность, поэтому
   * Apollo по умолчанию замещает его входящим целиком. Мутация
   * `updateActivityProfileParameters` возвращает `ActivityProfile!`, и операция
   * выбирает все семнадцать групп, так что в норме ответ полный и замещение
   * безвредно. Опасен усечённый ответ: он не «обнуляет соседей», а делает
   * промахом чтение всего объекта — карточки метаболизма схлопываются до «—»
   * до перезагрузки страницы. Ровно этот класс поломки девять карточек лечили
   * рефетчем после каждого сохранения.
   *
   * Слияние поверхностное и по группам: входящие группы перекрывают прежние,
   * остальные остаются на месте. Явный `null` во входящем — это стирание
   * (карточка с флажком «не знаю» шлёт в мутацию `null`), и он проходит, потому
   * что ключ во входящем объекте присутствует.
   */
  ActivityProfileParameters: {
    keyFields: false,
    merge(existing: Record<string, unknown> | undefined, incoming: Record<string, unknown>) {
      if (!existing) return incoming
      return { ...existing, ...incoming }
    },
  },
  // UserInfo (from getMe query) should be keyed by id to prevent cache collisions
  UserInfo: {
    keyFields: ["id"],
  },
  // Plan items are keyed by planID for proper cache normalization
  Plan: {
    keyFields: ["planID"],
    fields: {
      // Merge item array to avoid cache data loss warnings during deletions
      item: {
        merge(_existing: unknown, incoming: unknown) {
          return incoming
        },
      },
    },
  },
  /**
   * Normalized, at last — keyed by `id` like every other item.
   *
   * It took three levels underneath to get here, and the order was forced: a
   * shared record is only safe while every selection that writes a card brings
   * a `data` the others can live with. The grid reads WorkoutCardFragment /
   * MenuSummaryFragment, the editor reads the full selection through
   * `calendarItemFull`, statistics reads its own, and NutritionStatistics
   * fetches one menu by id. Inline, each query kept its own copy and they never
   * met; normalized, they all land on one record.
   *
   * So: the activity carries the id of the item holding it (schema), which makes
   * `data` a reference instead of an object to be replaced. The menu is a record
   * too, and the ingredient under it merges its `info` field by field, so the
   * slim answer stops taking the nutrients with it. Statistics asks for the same
   * PlanFactData as the calendar, so its answer updates the metrics instead of
   * trimming them. Each of those is pinned by a test of its own.
   *
   * What this buys: a mutation answering with the card updates the record by
   * itself, and every week holding a reference shows it. It does NOT remove the
   * hand-written writers in model/calendar/cache — putting a new card into a
   * day's list, and moving one between weeks, is work normalization has nothing
   * to say about.
   *
   * No explicit `keyFields`: the default keys it by `id` all the same and gives
   * the short form, `CalendarItem:<id>`, the same one the activities get. (A
   * spelled-out `keyFields: ["id"]` would produce `CalendarItem:{"id":"…"}` —
   * which is what PlanItem and ActivityLibraryItem carry, for no better reason
   * than that they say it out loud.)
   */
  CalendarItem: {
    fields: {
      // Visibility doesn't have an ID, so we need to merge it properly
      visibility: {
        merge(_existing: unknown, incoming: unknown) {
          return incoming
        },
      },
    },
  },
  /**
   * The counters of a calendar item's discussion — its badge — keyed by the item they count, as the
   * schema says (`Key: calendarItemId`). Every selection reaches them through the DiscussionSummary
   * fragment, which carries the key, so the answer of a feed page, of sending, editing or marking
   * read updates the one record the item's `discussion` points to. The counters are the viewer's
   * own while the key does not say whose: entering or leaving impersonation and logging out reload
   * the page, and the cache with it.
   */
  CalendarItemDiscussion: {
    keyFields: ["calendarItemId"],
  },
  /**
   * A video's and a file's own fields have no id, so an incoming object would replace the stored one whole
   * (@apollo/client cache/inmemory/writeToStore.js applyMerges). An answer selecting fewer of them — a poll of a
   * video's state — would then drop the link, and the message the asset hangs on would read incomplete and vanish
   * from its feed (docs/DISCUSSION_ATTACHMENTS_SPEC.md §4.1). Merged, the narrower answer updates what it carries.
   */
  AssetVideo: {
    merge: true,
  },
  AssetFile: {
    merge: true,
  },
  // PlanItem keyed by id for proper normalization
  PlanItem: {
    keyFields: ["id"],
  },
  // ActivityLibraryItem keyed by id for proper normalization
  ActivityLibraryItem: {
    keyFields: ["id"],
  },
}
